import mixpanel, { Config } from "mixpanel-browser";

import { TCurrentUser } from "@/data/api/kili/current-user/current-user.type";
import { getMixpanelConsentOnDidomiReady } from "@/didomi";
import { useCurrentUserStore } from "@/stores/current-user/current-user-store";
import { type Env } from "@/utils/env-schema";

export const MIXPANEL_VENDOR_NAME = "c:mixpanel";

class CMixpanelService {
  private static _instance: CMixpanelService;
  private _isMixpanelInitialized: boolean = false;
  private _isMixpanelOptIn: boolean = false;

  constructor() {}

  private optIn(): void {
    if (this._isMixpanelInitialized) mixpanel.opt_in_tracking();
  }

  public static getInstance() {
    if (!this._instance) {
      this._instance = new CMixpanelService();
    }
    return this._instance;
  }

  public async initialize(env: Env, options?: Config): Promise<void> {
    const mixpanelToken = env.VITE_MIXPANEL_TOKEN;

    if (mixpanelToken) {
      mixpanel.init(mixpanelToken, {
        persistence: "localStorage",
        api_host: "https://api-eu.mixpanel.com",
        opt_out_tracking_by_default: true,
        ...options,
      });

      this._isMixpanelInitialized = true;

      // in case consent has already been collected, tracking should be initialized as well
      const hasUserAllowedTracking = await getMixpanelConsentOnDidomiReady();
      MixpanelService.handleTrackingConsent(hasUserAllowedTracking);
    }
  }

  public identify(user: TCurrentUser) {
    if (this._isMixpanelInitialized && this._isMixpanelOptIn) {
      mixpanel.identify(user.userName);
      mixpanel.people.set({
        user_email: user.userName,
        organisation_sites_count: user.organizationAllSitesCount,
        user_sites_count: user.userPerimeterSitesCount,
        user_creation_date: user.creationDateTime,
        organization_name: user.organizationRootName,
        user_appliances_count: user.userPerimeterApplianceCount,
        is_operator: user.isOperator,
      });
    }
  }

  public handleTrackingConsent(hasUserAllowedTracking: boolean) {
    const currentUserStore = useCurrentUserStore();

    if (hasUserAllowedTracking) {
      if (!this._isMixpanelOptIn) {
        MixpanelService.optIn();
        this._isMixpanelOptIn = true;
      }

      if (currentUserStore.currentUser) {
        MixpanelService.identify(currentUserStore.currentUser);
      }
    }
  }

  public trackEvent(
    eventName: string,
    props?: Record<string, string | string[] | number | number[] | undefined>,
  ): void {
    if (this._isMixpanelInitialized) mixpanel.track(eventName, props);
  }

  public reset(): void {
    if (this._isMixpanelInitialized) {
      mixpanel.reset();
    }
  }
}

export const MixpanelService = CMixpanelService.getInstance();
